// ========================
// CHART OF ACCOUNTS
// ========================

export type LedgerGroup =
  | "Current Assets"
  | "Stock-in-Hand"
  | "Fixed Assets"
  | "Investments"
  | "Bank Accounts"
  | "Cash-in-Hand"
  | "Sundry Debtors"
  | "Sundry Creditors"
  | "Current Liabilities"
  | "Loans (Liability)"
  | "Capital Account"
  | "Reserves & Surplus"
  | "Revenue"
  | "Direct Income"
  | "Indirect Income"
  | "Direct Expenses"
  | "Indirect Expenses"
  | "Purchase Accounts"
  | "Sales Accounts"
  | "Duties & Taxes"
  | "Provisions"
  | "Secured Loans"
  | "Unsecured Loans"
  | "Branch / Divisions"
  | "Suspense A/c";

export type LedgerNature = "Asset" | "Liability" | "Income" | "Expense" | "Equity";

export const GROUP_NATURE: Record<LedgerGroup, LedgerNature> = {
  "Current Assets": "Asset",
  "Stock-in-Hand": "Asset",
  "Fixed Assets": "Asset",
  "Investments": "Asset",
  "Bank Accounts": "Asset",
  "Cash-in-Hand": "Asset",
  "Sundry Debtors": "Asset",
  "Sundry Creditors": "Liability",
  "Current Liabilities": "Liability",
  "Loans (Liability)": "Liability",
  "Capital Account": "Equity",
  "Reserves & Surplus": "Equity",
  "Revenue": "Income",
  "Direct Income": "Income",
  "Indirect Income": "Income",
  "Direct Expenses": "Expense",
  "Indirect Expenses": "Expense",
  "Purchase Accounts": "Expense",
  "Sales Accounts": "Income",
  "Duties & Taxes": "Liability",
  "Provisions": "Liability",
  "Secured Loans": "Liability",
  "Unsecured Loans": "Liability",
  "Branch / Divisions": "Asset",
  "Suspense A/c": "Asset",
};

export interface Ledger {
  id: string;
  name: string;
  group: LedgerGroup;
  nature: LedgerNature;
  openingBalance: number;
  openingBalanceType: "Dr" | "Cr";
  currentBalance: number;
  currentBalanceType: "Dr" | "Cr";
  gstApplicable: boolean;
  gstin?: string;
  hsnCode?: string;
  gstRate?: number;
  address?: string;
  state?: string;
  pincode?: string;
  contactPerson?: string;
  phone?: string;
  email?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  isSystem: boolean;
}

// ========================
// VOUCHER SYSTEM
// ========================

export type VoucherType =
  | "Sales"
  | "Purchase"
  | "Receipt"
  | "Payment"
  | "Journal"
  | "Contra"
  | "Credit Note"
  | "Debit Note"
  | "Stock Journal"
  | "Delivery Note"
  | "Receipt Note"
  | "Physical Stock";

export type VoucherStatus = "Draft" | "Pending" | "Approved" | "Rejected" | "Cancelled";

export interface InvoiceAllocation {
  invoiceId: string;
  invoiceNumber: string;
  allocatedAmount: number;
  discountAmount?: number;
}

export interface VoucherLine {
  id: string;
  ledgerId: string;
  ledgerName: string;
  debit: number;
  credit: number;
  narration?: string;
}

export interface InvoiceItem {
  id?: string;
  name: string;
  description?: string;
  hsnCode?: string;
  hsn?: string;
  sacCode?: string;
  qty: number;
  quantity?: number;
  rate: number;
  gstRate: number;
  amount: number;
  taxableAmount?: number;
  unit?: string;
}

export interface Voucher {
  id: string;
  voucherNumber: string;
  type: VoucherType;
  date: string; // YYYY-MM-DD (Transaction Date)
  lines: VoucherLine[];
  items?: InvoiceItem[];
  narration: string;
  totalDebit: number;
  totalCredit: number;
  isBalanced: boolean;
  status: VoucherStatus;
  partyLedgerId?: string;
  partyName?: string;
  invoiceNumber?: string;
  gstAmount?: number;
  cgst?: number;
  sgst?: number;
  igst?: number;
  createdBy: string;
  createdAt?: string; // System creation timestamp
  updatedAt?: string; // System update timestamp
  approvedBy?: string;
  approvedAt?: string;
  irn?: string;
  ackNo?: string;
  ackDate?: string;
  qrCodeData?: string;
  ewayBillNo?: string;
  ewayBillValidUntil?: string;

  // Comprehensive Payment & Instrument Details
  paymentMode?: string;
  instrumentNo?: string;
  instrumentDate?: string;
  bankName?: string;
  bankLedgerId?: string;
  allocations?: InvoiceAllocation[];
  paidAmount?: number;
  paymentStatus?: "Unpaid" | "Partially Paid" | "Paid";
  isBackdated?: boolean;
  reversedVoucherId?: string;
  cancellationReason?: string;
  reconciled?: boolean;
  reconciledAt?: string;
  reconciliationRef?: string;
}

// ========================
// INVENTORY
// ========================

export interface StockGroup {
  id: string;
  name: string;
  parentGroup?: string;
  createdAt: string;
}

export interface UnitOfMeasurement {
  id: string;
  symbol: string;
  formalName: string;
  decimalPlaces: number;
}

export type ItemType = "goods" | "service";

export type ServiceBillingMethod =
  | "one-time"
  | "hourly"
  | "daily"
  | "monthly"
  | "yearly"
  | "subscription"
  | "custom";

export interface StockItem {
  id: string;
  name: string;
  itemType: ItemType;
  stockGroup: string;
  unit: string;
  hsnCode?: string;
  sacCode?: string;
  billingMethod?: ServiceBillingMethod;
  standardRate?: number;
  costRate?: number;
  lastPurchaseRate?: number | null;
  lastPurchaseDate?: string | null;
  lastPurchaseParty?: string | null;
  lastPurchaseVoucher?: string | null;
  lastSellingRate?: number | null;
  lastSoldDate?: string | null;
  lastSoldParty?: string | null;
  lastSoldVoucher?: string | null;
  highestPurchaseRate?: number | null;
  lowestPurchaseRate?: number | null;
  highestSellingRate?: number | null;
  lowestSellingRate?: number | null;
  sellingRateVerified?: boolean;
  priceReviewFlag?: boolean;
  incomeLedgerId?: string;
  expenseLedgerId?: string;
  status?: "active" | "inactive";
  description?: string;
  gstRate: number;
  openingQty: number;
  openingRate: number;
  openingValue: number;
  currentQty: number;
  currentRate: number;
  currentValue: number;
  reorderLevel?: number;
  createdAt: string;
  updatedAt: string;
}

// ========================
// AUDIT TRAIL
// ========================

export type AuditAction = "Created" | "Updated" | "Deleted" | "Approved" | "Rejected" | "Cancelled" | "Viewed" | "Exported" | "Login" | "Logout";

export interface AuditFieldChange {
  field: string;
  oldValue: string;
  newValue: string;
}

export interface AuditEntry {
  id: string;
  timestamp: string;
  userId: string;
  userName: string;
  userRole: string;
  action: AuditAction;
  module: string;
  entityId: string;
  entityRef: string;
  details: string;
  ipAddress: string;
  sessionId?: string;
  previousData?: string;
  newData?: string;
  changes?: AuditFieldChange[];
  version?: number;
}

// ========================
// GST
// ========================

export interface GSTDetail {
  taxableAmount: number;
  cgstRate: number;
  cgstAmount: number;
  sgstRate: number;
  sgstAmount: number;
  igstRate: number;
  igstAmount: number;
  cessAmount: number;
  totalTax: number;
  totalAmount: number;
}

export function calculateGST(amount: number, rate: number, isInterState: boolean): GSTDetail {
  const taxableAmount = amount;
  const totalTaxRate = rate / 100;
  const totalTax = taxableAmount * totalTaxRate;

  if (isInterState) {
    return {
      taxableAmount,
      cgstRate: 0,
      cgstAmount: 0,
      sgstRate: 0,
      sgstAmount: 0,
      igstRate: rate,
      igstAmount: totalTax,
      cessAmount: 0,
      totalTax,
      totalAmount: taxableAmount + totalTax,
    };
  }

  const halfRate = rate / 2;
  const halfTax = totalTax / 2;
  return {
    taxableAmount,
    cgstRate: halfRate,
    cgstAmount: Math.round(halfTax * 100) / 100,
    sgstRate: halfRate,
    sgstAmount: Math.round(halfTax * 100) / 100,
    igstRate: 0,
    igstAmount: 0,
    cessAmount: 0,
    totalTax: Math.round(totalTax * 100) / 100,
    totalAmount: Math.round((taxableAmount + totalTax) * 100) / 100,
  };
}

// ========================
// E-WAY BILL
// ========================

export type EWayBillStatus = "Active" | "Expired" | "Cancelled" | "Draft";
export type TransportMode = "Road" | "Rail" | "Air" | "Ship";
export type SupplyType = "Outward" | "Inward";
export type SubSupplyType = "Supply" | "Export" | "Job Work" | "SKD/CKD" | "Recipient Not Known" | "Line Sales" | "Sales Return" | "Exhibition" | "Others";
export type DocumentType = "Tax Invoice" | "Bill of Supply" | "Bill of Entry" | "Challan" | "Credit Note" | "Debit Note" | "Others";

export interface EWayBill {
  id: string;
  ewayBillNumber: string;
  status: EWayBillStatus;
  supplyType: SupplyType;
  subSupplyType: SubSupplyType;
  documentType: DocumentType;

  // Linked voucher
  voucherId?: string;
  voucherNumber?: string;
  invoiceNumber?: string;
  invoiceDate: string;
  invoiceValue: number;

  // Party details
  fromGstin: string;
  fromName: string;
  fromAddress: string;
  fromState: string;
  fromPincode: string;
  toGstin: string;
  toName: string;
  toAddress: string;
  toState: string;
  toPincode: string;

  // Goods details
  hsnCode: string;
  productName: string;
  quantity: number;
  unit: string;
  taxableValue: number;
  cgstRate: number;
  sgstRate: number;
  igstRate: number;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
  cessAmount: number;

  // Transport details
  transportMode: TransportMode;
  transporterId?: string;
  transporterName?: string;
  transportDocNumber?: string;
  transportDocDate?: string;
  vehicleNumber?: string;
  vehicleType?: "Regular" | "Over Dimensional Cargo";

  // Distance & Validity
  distanceKm: number;
  validFrom: string;
  validUntil: string;

  // Metadata
  generatedAt: string;
  generatedBy: string;
  updatedAt: string;
  remarks?: string;
}

export function calculateEWayValidity(distanceKm: number): number {
  // As per GST rules: 1 day for first 200 km, then +1 day for every 200 km
  if (distanceKm <= 0) return 1;
  return Math.ceil(distanceKm / 200);
}
